import { useEffect, useState } from "react";
import { errorMessage, fetchPhoto, httpStatus } from "./api";

/** how long the photo of a just photographed pizza is waited for (the camera uploads it within seconds) */
const UPLOAD_WAIT_MS = 45_000;
const POLL_MS = 1_500;

export type PhotoState =
  | { kind: "loading" }
  | { kind: "ready"; url: string }
  | { kind: "missing" }
  | { kind: "failed"; message: string };

/** The camera's photo of the pizza as an object URL; with waitForUpload a 404 is asked again until the upload arrives. */
export function usePhoto(pizzaId: string, scale: number, waitForUpload = false): PhotoState {
  const [state, setState] = useState<PhotoState>({ kind: "loading" });

  useEffect(() => {
    const controller = new AbortController();
    const deadline = Date.now() + (waitForUpload ? UPLOAD_WAIT_MS : 0);
    let url: string | null = null;
    let timer: number | undefined;
    setState({ kind: "loading" });

    const load = async () => {
      try {
        url = URL.createObjectURL(await fetchPhoto(pizzaId, scale, controller.signal));
        setState({ kind: "ready", url });
      } catch (error) {
        if (controller.signal.aborted) return;
        if (httpStatus(error) !== 404) setState({ kind: "failed", message: errorMessage(error) });
        else if (Date.now() < deadline) timer = window.setTimeout(() => void load(), POLL_MS);
        else setState({ kind: "missing" });
      }
    };
    void load();

    return () => {
      controller.abort();
      window.clearTimeout(timer);
      if (url) URL.revokeObjectURL(url);
    };
  }, [pizzaId, scale, waitForUpload]);

  return state;
}
